import { useSelector } from 'store';
import { useGetOrgQuery, useUpdateOrgMutation } from 'store/slices/apiV1/org';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';
import { handleErr } from 'utils/functions';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import CardSectionWithAction from 'ui-component/cards/CardSectionWithAction';
import { FormControl, GridLegacy, Typography } from '@mui/material';
import { HookFormToggle } from 'ui-component/HookFormComponents';
import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import { ALL_APPS } from 'constants/appConstants';
import { appPermissionAccessLevels } from 'types/apps';
import SegmentButton from 'ui-component/Segment/SegmentButton';
import {
  formConstants,
  formValidation,
} from 'views/docai/Preferences/constants';
import useSnackbar from 'hooks/useSnackbar';
import { HookFormAutocompleteFreeSoloString } from 'ui-component/HookFormComponents/HookFormAutoCompleteFreeSoloString';

const DocAiPreferences = () => {
  const { dispatchSuccessSnackbar, dispatchErrorSnackbar } = useSnackbar();
  const [isEditing, setIsEditing] = useState(false);
  const { activeOrgId } = useSelector((state) => state.org);

  const { data: org } = useGetOrgQuery(activeOrgId ?? skipToken);
  const [updateOrg, { isLoading: isUpdatingOrg }] = useUpdateOrgMutation();

  const defaultValues = {
    [formConstants.docaiFilterWhitelistSuppliers.name]:
      org?.docaiFilterWhitelistSuppliers || true,
    [formConstants.docaiFilterDomainWhitelist.name]:
      org?.docaiFilterDomainWhitelist || [],
    [formConstants.docaiFilterDomainBlacklist.name]:
      org?.docaiFilterDomainBlacklist || [],
    [formConstants.docaiFilterSubjectRegexWhitelist.name]:
      org?.docaiFilterSubjectRegexWhitelist || [],
    [formConstants.docaiFilterSubjectRegexBlacklist.name]:
      org?.docaiFilterSubjectRegexBlacklist || [],
    [formConstants.docaiFilterBodyRegexWhitelist.name]:
      org?.docaiFilterBodyRegexWhitelist || [],
    [formConstants.docaiFilterAllowIfRecordMatches.name]:
      org?.docaiFilterAllowIfRecordMatches || true,
    [formConstants.docaiFilterIgnoreIfRecordNotMatched.name]:
      org?.docaiFilterIgnoreIfRecordNotMatched || false,
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(formValidation),
    mode: 'all',
  });

  useEffect(() => {
    if (org) {
      reset(defaultValues);
    }
  }, [org]);

  const onSubmit = async (data: FieldValues) => {
    if (isDirty) {
      try {
        await updateOrg({
          orgId: activeOrgId!,
          payload: data,
        }).unwrap();
        dispatchSuccessSnackbar('DocAI preferences updated');
        setIsEditing(false);
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    } else {
      setIsEditing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <CardSectionWithAction
        contents={
          <FormControl fullWidth>
            <GridLegacy container spacing={2}>
              <GridLegacy item xs={12}>
                <Typography>
                  These settings control all Inbound email integrations
                  configured within the Integrations App in Cofactr and all
                  emails forwarded to {`${org?.slug}@cofactr-ai.com`}. If an
                  incoming email matches any of the whitelist rules and none or
                  the blacklist rules, it will be processed by DocAI.
                </Typography>
              </GridLegacy>
              <GridLegacy item xs={12}>
                <HookFormToggle
                  name={formConstants.docaiFilterWhitelistSuppliers.name}
                  label={formConstants.docaiFilterWhitelistSuppliers.label}
                  helperText={
                    formConstants.docaiFilterWhitelistSuppliers.description
                  }
                  errors={errors}
                  control={control}
                  disabled={!isEditing}
                />
              </GridLegacy>
              <GridLegacy item xs={12}>
                <HookFormAutocompleteFreeSoloString
                  name={formConstants.docaiFilterDomainWhitelist.name}
                  label={formConstants.docaiFilterDomainWhitelist.label}
                  helperText={
                    formConstants.docaiFilterDomainWhitelist.description
                  }
                  control={control}
                  errors={errors}
                  options={[]}
                  disabled={!isEditing}
                />
              </GridLegacy>
              <GridLegacy item xs={12}>
                <HookFormAutocompleteFreeSoloString
                  name={formConstants.docaiFilterDomainBlacklist.name}
                  label={formConstants.docaiFilterDomainBlacklist.label}
                  helperText={
                    formConstants.docaiFilterDomainBlacklist.description
                  }
                  control={control}
                  errors={errors}
                  options={[]}
                  disabled={!isEditing}
                />
              </GridLegacy>
              <GridLegacy item xs={12}>
                <HookFormAutocompleteFreeSoloString
                  name={formConstants.docaiFilterSubjectRegexWhitelist.name}
                  label={formConstants.docaiFilterSubjectRegexWhitelist.label}
                  helperText={
                    formConstants.docaiFilterSubjectRegexWhitelist.description
                  }
                  control={control}
                  errors={errors}
                  options={[]}
                  disabled={!isEditing}
                />
              </GridLegacy>
              <GridLegacy item xs={12}>
                <HookFormAutocompleteFreeSoloString
                  name={formConstants.docaiFilterSubjectRegexBlacklist.name}
                  label={formConstants.docaiFilterSubjectRegexBlacklist.label}
                  helperText={
                    formConstants.docaiFilterSubjectRegexBlacklist.description
                  }
                  control={control}
                  errors={errors}
                  options={[]}
                  disabled={!isEditing}
                />
              </GridLegacy>
              <GridLegacy item xs={12}>
                <HookFormAutocompleteFreeSoloString
                  name={formConstants.docaiFilterBodyRegexWhitelist.name}
                  label={formConstants.docaiFilterBodyRegexWhitelist.label}
                  helperText={
                    formConstants.docaiFilterBodyRegexWhitelist.description
                  }
                  control={control}
                  errors={errors}
                  options={[]}
                  disabled={!isEditing}
                />
              </GridLegacy>
              <GridLegacy item xs={12}>
                <HookFormAutocompleteFreeSoloString
                  name={formConstants.docaiFilterBodyRegexBlacklist.name}
                  label={formConstants.docaiFilterBodyRegexBlacklist.label}
                  helperText={
                    formConstants.docaiFilterBodyRegexBlacklist.description
                  }
                  control={control}
                  errors={errors}
                  options={[]}
                  disabled={!isEditing}
                />
              </GridLegacy>
              <GridLegacy item xs={12}>
                <HookFormToggle
                  name={formConstants.docaiFilterAllowIfRecordMatches.name}
                  label={formConstants.docaiFilterAllowIfRecordMatches.label}
                  helperText={
                    formConstants.docaiFilterAllowIfRecordMatches.description
                  }
                  errors={errors}
                  control={control}
                  disabled={!isEditing}
                />
              </GridLegacy>
              <GridLegacy item xs={12}>
                <HookFormToggle
                  name={formConstants.docaiFilterIgnoreIfRecordNotMatched.name}
                  label={
                    formConstants.docaiFilterIgnoreIfRecordNotMatched.label
                  }
                  helperText={
                    formConstants.docaiFilterIgnoreIfRecordNotMatched
                      .description
                  }
                  errors={errors}
                  control={control}
                  disabled={!isEditing}
                />
              </GridLegacy>
            </GridLegacy>
          </FormControl>
        }
        action={
          isEditing ? (
            <SegmentLoadingButton
              type="submit"
              variant="contained"
              color="primary"
              size="medium"
              loading={isUpdatingOrg}
              permissionScope={{
                app: ALL_APPS.ORDERS.id,
                accessLevel: appPermissionAccessLevels.edit,
              }}
            >
              {isDirty ? 'Save' : 'Cancel'}
            </SegmentLoadingButton>
          ) : (
            <SegmentButton
              type="button"
              variant="contained"
              onClick={() => setIsEditing(true)}
              sx={{ float: 'right' }}
              permissionScope={{
                app: ALL_APPS.ORDERS.id,
                accessLevel: appPermissionAccessLevels.edit,
              }}
            >
              Edit
            </SegmentButton>
          )
        }
        title="Inbound Email Preferences"
      />
    </form>
  );
};

export default DocAiPreferences;
